/* =========================================================
   Lógica en memoria compartida por las dos fuentes de datos.

   Aquí viven las consultas, los filtros y las mutaciones sobre
   arreglos — todo lo que no depende de dónde salieron los datos.
   ApiDataSource hereda de esta clase: hidrata desde /api/bootstrap y
   propaga las escrituras al servidor, pero las consultas son estas.

   Nació para la maqueta, que sembraba estos arreglos con datos
   inventados. La maqueta se retiró; esto se queda, porque es lo que da
   las actualizaciones optimistas: que un cambio se vea al instante y se
   deshaga solo si el servidor lo rechaza.

   Los métodos `save*` son ganchos: la base no persiste nada.
   ========================================================= */
import type {
  AppUser, Assignment, ComplianceDoc, ComplianceSummary, DisassocRequest,
  Instructor, Lang, LegalRequest, Notification, Organization, Site,
  UserDraft, UserPatch, UserWriteResult,
} from '@/types';
import type {
  AssignmentFilter, ComplianceProgressPatch, DataSource, InstructorFilter,
  OrgFilter, SiteFilter, Stats, UserFilter,
} from './DataSource';
import type { ComplianceRegistry } from '@/types';

export class MemoryDataSource implements DataSource {
  orgs: Organization[] = [];
  sites: Site[] = [];
  instructors: Instructor[] = [];
  assignments: Assignment[] = [];
  appUsers: AppUser[] = [];
  compliance: ComplianceRegistry = {};
  disassocReqs: DisassocRequest[] = [];
  legalReqs: LegalRequest[] = [];
  notifications: Notification[] = [];


  /* ---------------- notification queue ---------------- */

  listNotifications() {
    return this.notifications;
  }

  unreadNotifications() {
    return this.notifications.filter((n) => !n.read).length;
  }

  addNotification(n: Omit<Notification, 'id' | 'ts' | 'read'> & Partial<Notification>) {
    this.notifications.unshift({
      id: Math.random().toString(36).slice(2),
      ts: Date.now(),
      read: false,
      ...n,
    } as Notification);
    if (this.notifications.length > 50) this.notifications.length = 50;
    this.saveNotifications();
  }

  markAllNotificationsRead() {
    this.notifications.forEach((n) => {
      n.read = true;
    });
    this.saveNotifications();
  }

  clearNotifications() {
    this.notifications = [];
    this.saveNotifications();
  }

  /** Gancho de persistencia. La base no guarda nada. */
  saveNotifications() {}

  /** En memoria no hay servidor del que releer. */
  async refreshNotifications(): Promise<void> {}

  /** Gancho de persistencia. La base no guarda nada. */
  protected saveLegal() {}

  /* ---------------- disassociation requests ---------------- */

  listDisassocRequests(filter: { instructorId?: string; siteId?: string } = {}) {
    return this.disassocReqs.filter(
      (r) =>
        (!filter.instructorId || r.InstructorId === filter.instructorId) &&
        (!filter.siteId || r.SiteId === filter.siteId),
    );
  }

  hasDisassocRequest(instructorId: string, siteId: string) {
    return this.disassocReqs.some((r) => r.InstructorId === instructorId && r.SiteId === siteId);
  }

  requestDisassociation(req: Omit<DisassocRequest, 'date'>) {
    if (this.hasDisassocRequest(req.InstructorId, req.SiteId)) return;
    this.disassocReqs.push({ ...req, date: new Date().toISOString().slice(0, 10) });
    this.saveDisassoc();
  }

  /** La mutación en memoria, sin pasar por el método público: igual que
   *  `quitarAfiliacion`, para no saltar a la versión de ApiDataSource. */
  protected quitarSolicitud(instructorId: string, siteId: string) {
    this.disassocReqs = this.disassocReqs.filter(
      (r) => !(r.InstructorId === instructorId && r.SiteId === siteId),
    );
    this.saveDisassoc();
  }

  cancelDisassociation(instructorId: string, siteId: string) {
    this.quitarSolicitud(instructorId, siteId);
  }

  authorizeDisassociation(instructorId: string, siteId: string): number {
    const quedan = this.quitarAfiliacion(instructorId, siteId);
    this.quitarSolicitud(instructorId, siteId);
    return quedan;
  }

  rejectDisassociation(instructorId: string, siteId: string, _reason: string) {
    this.quitarSolicitud(instructorId, siteId);
  }

  /** En memoria no hay nada que releer. */
  async refreshRequests(): Promise<void> {}

  /** Gancho de persistencia. La base no guarda nada. */
  protected saveDisassoc() {}

  /* ---------------- legal contact change requests ---------------- */

  listLegalRequests() {
    return this.legalReqs;
  }

  async addLegalRequest(
    req: Omit<LegalRequest, 'id' | 'date' | 'status'>, carta?: File | null,
  ): Promise<LegalRequest> {
    const full: LegalRequest = {
      id: 'LR-' + Date.now().toString(36),
      date: new Date().toISOString().slice(0, 10),
      status: 'pending',
      ...req,
      // en la demo no hay dónde guardarlo, pero el nombre sí queda
      doc: carta?.name ?? req.doc,
      docStored: false,
    };
    this.legalReqs.unshift(full);
    this.saveLegal();
    return full;
  }

  /** Sin cuenta en una base no hay dónde guardarlo: se queda en el
   *  navegador, que es lo que hace `setLanguage`. */
  async setLang(_lang: Lang): Promise<void> {}

  /** En la demo no hay archivo que servir: solo se guardó el nombre. */
  async loadLegalDoc(_id: string): Promise<string> {
    throw new Error('La carta de nombramiento no está disponible en la demostración.');
  }

  /** Rejection always carries a reason; it is stored with the resolution. */
  resolveLegalRequest(id: string, status: 'approved' | 'rejected', rejectReason?: string) {
    const r = this.legalReqs.find((x) => x.id === id);
    if (!r) return;
    r.status = status;
    r.resolvedDate = new Date().toISOString().slice(0, 10);
    if (status === 'rejected') r.rejectReason = rejectReason || '';
    this.saveLegal();
  }

  /* ---------------- Compliance & best practices ---------------- */
  // Unlimited list of documents per org; each tracks its own verification.

  listCompliance(orgId: string) {
    return this.compliance[orgId] || [];
  }

  getCompliance(orgId: string): ComplianceSummary | null {
    const docs = this.compliance[orgId];
    if (!docs || !docs.length) return null;
    return {
      count: docs.length,
      verified: docs.filter((d) => d.pct === 100).length,
      pct: Math.round(docs.reduce((s, d) => s + (d.pct || 0), 0) / docs.length),
    };
  }

  /** Lee el archivo como data URL: es lo que sabe abrir el visor sin
   *  servidor detrás. */
  protected static async aDataUrl(file: File): Promise<string> {
    return new Promise((ok, fail) => {
      const r = new FileReader();
      r.onload = () => ok(String(r.result));
      r.onerror = () => fail(new Error('No se pudo leer el archivo'));
      r.readAsDataURL(file);
    });
  }

  async loadComplianceFile(orgId: string, docId: string): Promise<string> {
    const d = (this.compliance[orgId] || []).find((x) => x.id === docId);
    if (!d?.dataUrl) throw new Error('El documento no tiene contenido');
    return d.dataUrl;
  }

  async addComplianceDoc(orgId: string, file: File, title?: string): Promise<ComplianceDoc> {
    const meta = {
      name: file.name,
      size: file.size,
      date: new Date().toISOString().slice(0, 10),
      dataUrl: await MemoryDataSource.aDataUrl(file),
      ...(title ? { title } : {}),
    };
    const docs = this.compliance[orgId] || (this.compliance[orgId] = []);
    const doc: ComplianceDoc = {
      id: 'CMP-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 7),
      title: meta.name.replace(/\.(pdf|docx)$/i, '').replace(/[_-]+/g, ' '),
      kind: /\.docx$/i.test(meta.name) ? 'docx' : 'pdf',
      total: 0,
      pages: [],
      pct: 0,
      verifiedDate: null,
      verifiedAt: null,
      history: [],
      user: true,
      ...meta,
    };
    docs.unshift(doc);
    doc.sessionOnly = !this.saveCompUser(orgId);
    return doc;
  }

  removeComplianceDoc(orgId: string, docId: string) {
    const docs = this.compliance[orgId];
    if (!docs) return;
    this.compliance[orgId] = docs.filter((d) => d.id !== docId);
    this.saveCompUser(orgId);
    this.saveCompProg(orgId);
  }

  setComplianceProgress(
    orgId: string,
    docId: string,
    { pct, pages, total }: ComplianceProgressPatch = {},
  ): ComplianceDoc | null {
    const d = (this.compliance[orgId] || []).find((x) => x.id === docId);
    if (!d) return null;
    if (Array.isArray(pages)) d.pages = pages;
    if (total != null) d.total = total;
    if (pct != null) d.pct = pct;
    else if (d.total) d.pct = Math.round((d.pages.length / d.total) * 100);

    // verification completed = the exact moment the review reached 100%;
    // every completion is appended to the document's history.
    if (d.pct >= 100 && !d.verifiedAt) {
      d.verifiedAt = new Date().toISOString();
      d.verifiedDate = d.verifiedAt.slice(0, 10);
      d.history = (d.history || []).concat(d.verifiedAt);
    }
    if (d.pct < 100) {
      d.verifiedAt = null;
      d.verifiedDate = null;
    }
    this.saveCompProg(orgId);
    return d;
  }

  /** Gancho de persistencia. Devuelve si el documento sobrevive a una recarga. */
  protected saveCompUser(_orgId: string): boolean {
    return true;
  }

  /** Gancho de persistencia. La base no guarda nada. */
  protected saveCompProg(_orgId: string) {}

  /* ---------------- queries ---------------- */

  listOrgs(filter: OrgFilter = {}) {
    return this.orgs.filter(
      (o) => !filter.q || (o.OrgName + o.OrgId).toLowerCase().includes(filter.q.toLowerCase()),
    );
  }

  getOrg(orgId: string) {
    return this.orgs.find((o) => o.OrgId === orgId);
  }

  listSites(filter: SiteFilter = {}) {
    return this.sites.filter((s) => {
      if (filter.orgId && s.OrgId !== filter.orgId) return false;
      if (filter.orgIds && !filter.orgIds.includes(s.OrgId)) return false;
      if (filter.q && !(s.SiteName + s.SiteId + s.OrgName).toLowerCase().includes(filter.q.toLowerCase())) return false;
      return true;
    });
  }

  getSite(siteId: string) {
    return this.sites.find((s) => s.SiteId === siteId);
  }

  listInstructors(filter: InstructorFilter = {}) {
    return this.instructors.filter((i) => {
      if (filter.disassociated === true && !i.disassociated) return false;
      if (filter.disassociated === false && i.disassociated) return false;
      if (filter.siteId) {
        const asg = this.assignments.find(
          (a) => a.InstructorId === i.InstructorId && a.SiteId === filter.siteId,
        );
        if (!asg) return false;
      }
      if (
        filter.q &&
        !(i.EnglishFirstName + ' ' + i.EnglishLastName + i.InstructorId + i.EmailAddress)
          .toLowerCase()
          .includes(filter.q.toLowerCase())
      ) {
        return false;
      }
      return true;
    });
  }

  getInstructor(instructorId: string) {
    return this.instructors.find((i) => i.InstructorId === instructorId);
  }

  listAssignments(filter: AssignmentFilter = {}) {
    return this.assignments.filter((a) => {
      if (filter.instructorId && a.InstructorId !== filter.instructorId) return false;
      if (filter.siteId && a.SiteId !== filter.siteId) return false;
      if (filter.orgId && a.OrgId !== filter.orgId) return false;
      if (filter.orgIds && !filter.orgIds.includes(a.OrgId)) return false;
      return true;
    });
  }

  /** Instructor leaves the site; with no assignments left they become disassociated.
   *
   *  Está aparte de `removeAssignment` a propósito: los métodos públicos
   *  los sobrescribe ApiDataSource para hablar con el servidor, así que
   *  llamarlos desde aquí saltaría a la versión del hijo. Este no se
   *  sobrescribe nunca — es la mutación en memoria, y nada más. */
  protected quitarAfiliacion(instructorId: string, siteId: string): number {
    this.assignments = this.assignments.filter(
      (a) => !(a.InstructorId === instructorId && a.SiteId === siteId),
    );
    const left = this.assignments.filter((a) => a.InstructorId === instructorId).length;
    const i = this.getInstructor(instructorId);
    if (i && left === 0) i.disassociated = true;
    return left;
  }

  removeAssignment(instructorId: string, siteId: string): number {
    return this.quitarAfiliacion(instructorId, siteId);
  }

  /** Assigns an instructor to a site in any org; clears the disassociated flag. */
  addAssignment(instructorId: string, siteId: string, primaryRoleId?: string): Assignment | null {
    const site = this.getSite(siteId);
    const instr = this.getInstructor(instructorId);
    if (!site || !instr) return null;
    if (this.assignments.some((a) => a.InstructorId === instructorId && a.SiteId === siteId)) return null;
    const asg: Assignment = {
      InstructorId: instructorId,
      OrgId: site.OrgId,
      OrgName: site.OrgName,
      SiteId: site.SiteId,
      SiteName: site.SiteName,
      SiteCountry: site.SiteCountry,
      PrimaryRoleId: primaryRoleId || 'Instructor',
      Status: 'active',
    };
    this.assignments.push(asg);
    instr.disassociated = false;
    return asg;
  }

  /* ---------------- managed users ---------------- */

  listUsers(filter: UserFilter = {}) {
    return this.appUsers.filter((u) => {
      if (filter.orgId && u.orgId !== filter.orgId) return false;
      if (filter.siteId && u.siteId !== filter.siteId) return false;
      if (filter.role && u.role !== filter.role) return false;
      if (filter.status && u.status !== filter.status) return false;
      if (filter.q) {
        const hay = `${u.FirstName} ${u.LastName} ${u.Email} ${u.id} ${u.role || ''}`.toLowerCase();
        if (!hay.includes(filter.q.toLowerCase())) return false;
      }
      return true;
    });
  }

  getUser(id: string) {
    return this.appUsers.find((u) => u.id === id);
  }

  // The signatures are asynchronous because ApiDataSource has to wait for
  // the server; in memory the answer is already there.

  async createUser(obj: UserDraft): Promise<UserWriteResult> {
    const n = 40000 + this.appUsers.length + 1;
    // the password is not kept anywhere: only that one was set
    const { password, ...rest } = obj;
    const u: AppUser = { id: 'USR-' + String(n).padStart(4, '0'), ...rest };
    if (password) u.auth = { ...u.auth, password: true };
    this.appUsers.unshift(u);
    return { record: u, notes: [] };
  }

  async updateUser(id: string, patch: UserPatch): Promise<UserWriteResult> {
    const u = this.getUser(id);
    if (!u) throw new Error(`No existe la cuenta ${id}`);
    const { password, ...rest } = patch;
    Object.assign(u, rest);
    if (password) u.auth = { ...u.auth, password: true };
    return { record: u, notes: [] };
  }

  /* ---------------- mutations ---------------- */
  // In-memory here; these become HTTP calls in ApiDataSource.

  updateOrg(orgId: string, patch: Partial<Organization>) {
    const o = this.getOrg(orgId);
    if (o) Object.assign(o, patch);
  }

  updateSite(siteId: string, patch: Partial<Site>) {
    const s = this.getSite(siteId);
    if (s) Object.assign(s, patch);
  }

  updateInstructor(instructorId: string, patch: Partial<Instructor>) {
    const i = this.getInstructor(instructorId);
    if (i) Object.assign(i, patch);
  }

  deactivateInstructor(instructorId: string) {
    const i = this.getInstructor(instructorId);
    if (i) i.active = false;
  }

  /* ---------------- metrics ---------------- */

  stats(): Stats {
    return {
      orgs: this.orgs.length,
      sites: this.sites.length,
      instructors: this.instructors.filter((i) => !i.disassociated).length,
      disassociated: this.instructors.filter((i) => i.disassociated).length,
      sitesWithRed: this.sites.filter(
        (s) => !s.RegisteredPostalCode || !s.CommercialSiteName || !s.Workstations || s.SitePostalCode === '0000',
      ).length,
    };
  }
}
